<script setup lang="ts">
  import type { CustomerCase } from '@/types/generated/types.gen'
  import { computed, ref } from 'vue'
  import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger
  } from '@/components/ui/dropdown-menu'
  import { Button } from '@/components/ui/button'
  import { ChevronDown, CircleCheck, Menu } from 'lucide-vue-next'
  import { useTranslation } from '@/composables/useTranslation'
  import ActaportCollisionModal from '@/components/app/cases/ActaportCollisionModal.vue'
  import { useUserStore } from '@/store/userStore'

  interface Props {
    caseData: CustomerCase
  }

  const props = defineProps<Props>()

  // Check screen width for responsive display
  const isSmallScreen = ref(window.innerWidth < 768)

  // Update screen size on resize
  window.addEventListener('resize', () => {
    isSmallScreen.value = window.innerWidth < 768
  })

  // Translations
  const { t } = useTranslation({
    en: {
      lawFirmSoftwareExport: 'Law Firm Software Export',
      actaport: 'Actaport',
      advolux: 'Advolux',
      raMicro: 'RA-MICRO',
      advoware: 'Advoware',
      kleos: 'Kleos',
      jurNodes: 'JurNodes',
      notExported: 'Not exported',
      fileNumber: 'File number #{id}',
      checkConflict: 'Check Conflict of Interest',
      caseActions: 'Case Actions'
    },
    de: {
      lawFirmSoftwareExport: 'Kanzleisoftware Export',
      actaport: 'Actaport',
      advolux: 'Advolux',
      raMicro: 'RA-MICRO',
      advoware: 'Advoware',
      kleos: 'Kleos',
      jurNodes: 'JurNodes',
      notExported: 'Nicht exportiert',
      fileNumber: 'Aktenzeichen #{id}',
      checkConflict: 'Interessenskollision prüfen',
      caseActions: 'Aktionen'
    }
  })

  // Modals state
  const actaportCollisionModalOpen = ref(false)
  const userStore = useUserStore()

  // Computed properties to check which integrations are enabled
  const integrations = computed(() => {
    const data = userStore.user?.org
    return {
      lawFirmSoftwareExport: data?.is_law_firm_software_exportation_enabled || false,
      actaport: data?.is_actaport_export_integration_enabled || false,
      advolux: data?.is_advolux_export_integration_enabled || false,
      raMicro: data?.is_ra_micro_export_integration_enabled || false,
      advoware: data?.is_advoware_export_integration_enabled || false,
      kleos: data?.is_kleos_integration_enabled || false,
      jurNodes: data?.is_jurnodes_export_integration_enabled || false
    }
  })

  // Integration states
  const integrationStates = computed(() => {
    const data = props.caseData
    return {
      actaport: {
        caseId: data?.actaport_case_id,
        hasCase: !!data?.actaport_case_id
      },
      advolux: {
        caseId: data?.advolux_case_id,
        hasCase: !!data?.advolux_case_id
      },
      raMicro: {
        caseId: data?.ra_micro_case_id,
        hasCase: !!data?.ra_micro_case_id
      },
      advoware: {
        caseId: data?.advoware_case_id,
        hasCase: !!data?.advoware_case_id
      },
      kleos: {
        caseId: data?.kleos_case_id,
        hasCase: !!data?.kleos_case_id
      },
      jurNodes: {
        caseId: data?.jurnodes_case_id,
        hasCase: !!data?.jurnodes_case_id
      }
    }
  })

  const handleExport = (integration: string) => {
    const host = import.meta.env.VITE_HOST || ''
    const caseUuid = props.caseData.uuid
    window.location.href = `${host}/app/integrations/${integration}/export/${caseUuid}`
  }

  const openActaportCase = (caseId: string | null | undefined) => {
    if (caseId) {
      // Replace slash with hyphen in the case ID
      const formattedCaseId = caseId.replace('/', '-')
      window.open(`https://app.actaport.de/akte/${formattedCaseId}/dashboard`, '_blank')
    }
  }

  const openCollisionCheck = (integration: string) => {
    switch (integration) {
      case 'actaport':
        actaportCollisionModalOpen.value = true
        break
    }
  }
</script>

<template>
  <!-- Desktop View -->
  <div class="hidden gap-2 md:flex">
    <!-- Kanzleisoftware Export -->
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          class="rounded-lg"
        >
          <span>{{ t('lawFirmSoftwareExport') }}</span>
          <ChevronDown class="ml-2 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        class="w-[400px] min-w-[--radix-dropdown-menu-trigger-width] rounded-lg p-0"
        :align="'start'"
        :side-offset="1"
      >
        <!-- Actaport -->
        <div
          class="cursor-pointer border-gray-100 p-3 hover:bg-gray-50"
          @click="
            integrationStates.actaport.hasCase
              ? openActaportCase(integrationStates.actaport.caseId)
              : handleExport('actaport')
          "
        >
          <div class="flex items-center justify-between gap-3">
            <span class="font-normal">{{ t('actaport') }}</span>
            <span
              v-if="!integrationStates.actaport.hasCase"
              class="text-xs text-gray-400"
            >
              {{ t('notExported') }}
            </span>
            <div
              v-else
              class="flex items-center gap-2"
            >
              <CircleCheck class="h-4 w-4 text-green-600" />
              <span class="text-sm font-normal text-green-600">
                {{ t('fileNumber', { id: integrationStates.actaport.caseId }) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Advolux -->
        <div
          v-if="integrations.advolux"
          class="cursor-pointer border-gray-100 p-3 hover:bg-gray-50"
          @click="handleExport('advolux')"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="font-normal">{{ t('advolux') }}</span>
            <span
              v-if="!integrationStates.advolux.hasCase"
              class="text-xs text-gray-400"
            >
              {{ t('notExported') }}
            </span>
            <div
              v-else
              class="flex items-center gap-2"
            >
              <CircleCheck class="h-4 w-4 text-green-600" />
              <span class="text-sm font-normal text-green-600">
                {{ t('fileNumber', { id: integrationStates.advolux.caseId }) }}
              </span>
            </div>
          </div>
        </div>

        <!-- RA-MICRO -->
        <div
          v-if="integrations.raMicro"
          class="cursor-pointer border-gray-100 p-3 hover:bg-gray-50"
          @click="handleExport('raMicro')"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="font-normal">{{ t('raMicro') }}</span>
            <span
              v-if="!integrationStates.raMicro.hasCase"
              class="text-xs text-gray-400"
            >
              {{ t('notExported') }}
            </span>
            <div
              v-else
              class="flex items-center gap-2"
            >
              <CircleCheck class="h-4 w-4 text-green-600" />
              <span class="text-sm font-normal text-green-600">
                {{ t('fileNumber', { id: integrationStates.raMicro.caseId }) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Advoware -->
        <div
          v-if="integrations.advoware"
          class="cursor-pointer border-gray-100 p-3 hover:bg-gray-50"
          @click="handleExport('advoware')"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="font-normal">{{ t('advoware') }}</span>
            <span
              v-if="!integrationStates.advoware.hasCase"
              class="text-xs text-gray-400"
            >
              {{ t('notExported') }}
            </span>
            <div
              v-else
              class="flex items-center gap-2"
            >
              <CircleCheck class="h-4 w-4 text-green-600" />
              <span class="text-sm font-normal text-green-600">
                {{ t('fileNumber', { id: integrationStates.advoware.caseId }) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Kleos -->
        <div
          v-if="integrations.kleos"
          class="cursor-pointer border-gray-100 p-3 hover:bg-gray-50"
          @click="handleExport('kleos')"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="font-normal">{{ t('kleos') }}</span>
            <span
              v-if="!integrationStates.kleos.hasCase"
              class="text-xs text-gray-400"
            >
              {{ t('notExported') }}
            </span>
            <div
              v-else
              class="flex items-center gap-2"
            >
              <CircleCheck class="h-4 w-4 text-green-600" />
              <span class="text-sm font-normal text-green-600">
                {{ t('fileNumber', { id: integrationStates.kleos.caseId }) }}
              </span>
            </div>
          </div>
        </div>

        <!-- JurNodes -->
        <div
          v-if="integrations.jurNodes"
          class="cursor-pointer p-3 hover:bg-gray-50"
          @click="handleExport('jurNodes')"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="font-medium">{{ t('jurNodes') }}</span>
            <span
              v-if="!integrationStates.jurNodes.hasCase"
              class="text-xs text-gray-400"
            >
              {{ t('notExported') }}
            </span>
            <div
              v-else
              class="flex items-center gap-2"
            >
              <CircleCheck class="h-4 w-4 text-green-600" />
              <span class="text-sm font-normal text-green-600">
                {{ t('fileNumber', { id: integrationStates.jurNodes.caseId }) }}
              </span>
            </div>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>

    <!-- Interessenkollision -->
    <Button
      v-if="integrations.actaport"
      variant="secondary"
      class="rounded-lg"
      @click="openCollisionCheck('actaport')"
    >
      {{ t('checkConflict') }}
    </Button>
  </div>

  <!-- Mobile View - Combined Dropdown -->
  <div class="md:hidden">
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="secondary"
          class="rounded-lg"
        >
          <Menu class="mr-1 h-4 w-4" />
          <span class="text-xs">{{ t('caseActions') }}</span>
          <ChevronDown class="ml-1 h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        class="mt-2 w-[300px] rounded-lg p-0"
        :align="'start'"
        :side-offset="1"
      >
        <!-- Header for software export -->
        <div class="border-b border-gray-100 bg-gray-50 px-3 py-2">
          <span class="text-sm font-medium">{{ t('lawFirmSoftwareExport') }}</span>
        </div>

        <!-- Actaport -->
        <div
          v-if="integrations.actaport"
          class="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50"
          @click="
            integrationStates.actaport.hasCase
              ? openActaportCase(integrationStates.actaport.caseId)
              : handleExport('actaport')
          "
        >
          <div class="flex items-center justify-between gap-3">
            <span class="font-normal">{{ t('actaport') }}</span>
            <span
              v-if="!integrationStates.actaport.hasCase"
              class="text-xs text-gray-400"
            >
              {{ t('notExported') }}
            </span>
            <div
              v-else
              class="flex items-center gap-2"
            >
              <CircleCheck class="h-4 w-4 text-green-600" />
              <span class="text-sm font-normal text-green-600">
                {{ t('fileNumber', { id: integrationStates.actaport.caseId }) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Advolux -->
        <div
          v-if="integrations.advolux"
          class="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50"
          @click="handleExport('advolux')"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="font-normal">{{ t('advolux') }}</span>
            <span
              v-if="!integrationStates.advolux.hasCase"
              class="text-xs text-gray-400"
            >
              {{ t('notExported') }}
            </span>
            <div
              v-else
              class="flex items-center gap-2"
            >
              <CircleCheck class="h-4 w-4 text-green-600" />
              <span class="text-sm font-normal text-green-600">
                {{ t('fileNumber', { id: integrationStates.advolux.caseId }) }}
              </span>
            </div>
          </div>
        </div>

        <!-- RA-MICRO -->
        <div
          v-if="integrations.raMicro"
          class="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50"
          @click="handleExport('raMicro')"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="font-normal">{{ t('raMicro') }}</span>
            <span
              v-if="!integrationStates.raMicro.hasCase"
              class="text-xs text-gray-400"
            >
              {{ t('notExported') }}
            </span>
            <div
              v-else
              class="flex items-center gap-2"
            >
              <CircleCheck class="h-4 w-4 text-green-600" />
              <span class="text-sm font-normal text-green-600">
                {{ t('fileNumber', { id: integrationStates.raMicro.caseId }) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Advoware -->
        <div
          v-if="integrations.advoware"
          class="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50"
          @click="handleExport('advoware')"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="font-normal">{{ t('advoware') }}</span>
            <span
              v-if="!integrationStates.advoware.hasCase"
              class="text-xs text-gray-400"
            >
              {{ t('notExported') }}
            </span>
            <div
              v-else
              class="flex items-center gap-2"
            >
              <CircleCheck class="h-4 w-4 text-green-600" />
              <span class="text-sm font-normal text-green-600">
                {{ t('fileNumber', { id: integrationStates.advoware.caseId }) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Kleos -->
        <div
          v-if="integrations.kleos"
          class="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50"
          @click="handleExport('kleos')"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="font-normal">{{ t('kleos') }}</span>
            <span
              v-if="!integrationStates.kleos.hasCase"
              class="text-xs text-gray-400"
            >
              {{ t('notExported') }}
            </span>
            <div
              v-else
              class="flex items-center gap-2"
            >
              <CircleCheck class="h-4 w-4 text-green-600" />
              <span class="text-sm font-normal text-green-600">
                {{ t('fileNumber', { id: integrationStates.kleos.caseId }) }}
              </span>
            </div>
          </div>
        </div>

        <!-- JurNodes -->
        <div
          v-if="integrations.jurNodes"
          class="cursor-pointer border-b border-gray-100 p-3 hover:bg-gray-50"
          @click="handleExport('jurNodes')"
        >
          <div class="flex items-center justify-between gap-3">
            <span class="font-normal">{{ t('jurNodes') }}</span>
            <span
              v-if="!integrationStates.jurNodes.hasCase"
              class="text-xs text-gray-400"
            >
              {{ t('notExported') }}
            </span>
            <div
              v-else
              class="flex items-center gap-2"
            >
              <CircleCheck class="h-4 w-4 text-green-600" />
              <span class="text-sm font-normal text-green-600">
                {{ t('fileNumber', { id: integrationStates.jurNodes.caseId }) }}
              </span>
            </div>
          </div>
        </div>

        <!-- Separator for additional options -->
        <div class="border-1 border-t border-gray-100"></div>

        <!-- Interessenkollision -->
        <div
          v-if="integrations.actaport"
          class="cursor-pointer p-3 hover:bg-gray-50"
          @click="openCollisionCheck('actaport')"
        >
          <span class="font-medium">{{ t('checkConflict') }}</span>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  </div>

  <!-- Actaport Collision Modal -->
  <ActaportCollisionModal v-model:show="actaportCollisionModalOpen" />
</template>
